import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Avatar,
  Tooltip
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import VideocamIcon from '@mui/icons-material/Videocam';
import InfoIcon from '@mui/icons-material/Info';
import RemoveIcon from '@mui/icons-material/Remove';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import MessageList from './MessageList';
import Composer from './Composer';
import { MSN } from './msnTheme';
import { getAuthenticatedUploadUrl } from '../../api/client';

const MIN_WIDTH = 320;
const MIN_HEIGHT = 300;
const MAX_WIDTH = typeof window !== 'undefined' ? Math.round(window.innerWidth * 0.9) : 800;
const MAX_HEIGHT = typeof window !== 'undefined' ? Math.round(window.innerHeight * 0.9) : 700;

/**
 * Janela de conversa.
 * - floating: redimensionável + minimizável (MSN)
 * - dock (minimized): barra ~250×40
 * - embedded: painel direito Dual-Pane (100%, sem resize)
 */
export default function ChatWindow({
  conversationId,
  contact,
  isGroup,
  groupName,
  messages,
  currentUserId,
  typingLabel,
  onClose,
  onSendMessage,
  markRead,
  sendTypingStart,
  sendTypingStop,
  onStartVideoCall,
  onStartGroupVideoCall,
  activeGroupCall,
  onJoinGroupVideoCall,
  inGroupCall,
  currentUser,
  fotoPerfil,
  contactStatus = 'offline',
  currentUserStatus = 'offline',
  minimized = false,
  onMinimize,
  width = 420,
  height = 520,
  onResize,
  embedded = false,
  onExpand
}) {
  const title = isGroup ? (groupName || 'Grupo') : (contact?.nome || contact?.email || 'Contato');
  const subtitle = !isGroup && contact?.email ? contact.email : null;
  const contactBorder = MSN.statusBorder[contactStatus] || MSN.statusBorder.offline;
  const userBorder = MSN.statusBorder[currentUserStatus] || MSN.statusBorder.offline;
  const allowResize = !embedded && !minimized && typeof onResize === 'function';

  const [resizing, setResizing] = useState(null);
  const [startSize, setStartSize] = useState({ w: width, h: height });
  const [startPos, setStartPos] = useState({ x: 0, y: 0 });
  const composerRef = useRef(null);
  const [dropHover, setDropHover] = useState(false);
  const dropDepthRef = useRef(0);

  const clamp = (v, min, max) => Math.min(max, Math.max(min, v));

  const handleConvDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dropDepthRef.current += 1;
    if (e.dataTransfer?.types?.includes('Files')) setDropHover(true);
  };

  const handleConvDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dropDepthRef.current = Math.max(0, dropDepthRef.current - 1);
    if (dropDepthRef.current === 0) setDropHover(false);
  };

  const handleConvDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
  };

  const handleConvDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dropDepthRef.current = 0;
    setDropHover(false);
    const files = e.dataTransfer?.files;
    if (files?.length) composerRef.current?.attachFiles?.(files);
  };

  const handleConvPaste = (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (const item of items) {
      if (item.kind === 'file' && item.type.startsWith('image/')) {
        e.preventDefault();
        const blob = item.getAsFile?.();
        if (!blob) return;
        const ext = (blob.type || 'image/png').split('/')[1] || 'png';
        const file = new File([blob], `print-${Date.now()}.${ext}`, { type: blob.type || 'image/png' });
        composerRef.current?.attachFiles?.([file]);
        return;
      }
    }
  };

  const handleResizeStart = useCallback(
    (e, direction) => {
      if (!allowResize) return;
      e.preventDefault();
      e.stopPropagation();
      setResizing(direction);
      setStartSize({ w: width, h: height });
      setStartPos({ x: e.clientX, y: e.clientY });
    },
    [allowResize, width, height]
  );

  useEffect(() => {
    if (!resizing || !onResize || !allowResize) return;
    const move = (e) => {
      const dx = e.clientX - startPos.x;
      const dy = e.clientY - startPos.y;
      let w = startSize.w;
      let h = startSize.h;
      if (resizing.includes('e')) w = clamp(startSize.w + dx, MIN_WIDTH, MAX_WIDTH);
      if (resizing.includes('s')) h = clamp(startSize.h + dy, MIN_HEIGHT, MAX_HEIGHT);
      onResize(w, h);
    };
    const up = () => setResizing(null);
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
    return () => {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
    };
  }, [resizing, startPos, startSize, onResize, allowResize]);

  const headerBar = (
    <Box
      sx={{
        bgcolor: embedded ? 'background.paper' : undefined,
        background: embedded ? undefined : MSN.titleBar,
        px: embedded ? 2 : 1.25,
        py: embedded ? 1.5 : 1.25,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        borderBottom: minimized ? 'none' : '1px solid',
        borderColor: embedded ? 'divider' : 'rgba(0,0,0,0.1)',
        flexShrink: 0,
        minHeight: minimized ? 40 : undefined,
        boxShadow: embedded ? 'none' : 'inset 0 1px 0 rgba(255,255,255,0.4)'
      }}
    >
      {embedded && !isGroup && (
        <Avatar
          src={contact?.fotoPerfil ? getAuthenticatedUploadUrl(contact.fotoPerfil) : undefined}
          sx={{ width: 36, height: 36, bgcolor: 'primary.main', fontSize: 14 }}
        >
          {(contact?.nome || '?').charAt(0).toUpperCase()}
        </Avatar>
      )}
      <Typography
        component="span"
        sx={{
          color: embedded ? 'text.primary' : MSN.titleBarText,
          fontSize: 13,
          fontWeight: 600,
          flex: 1,
          minWidth: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }}
      >
        {embedded ? (
          <>
            {title}
            {subtitle && (
              <Typography component="span" sx={{ ml: 1, fontSize: 12, color: 'text.secondary', fontWeight: 400 }}>
                {subtitle}
              </Typography>
            )}
          </>
        ) : (
          <>
            Para: <strong>{title}</strong>
            {subtitle && <span style={{ marginLeft: 4, fontSize: 12, opacity: 0.9 }}>&lt;{subtitle}&gt;</span>}
          </>
        )}
      </Typography>
      {!minimized && !isGroup && (
        <Tooltip title="Chamada de vídeo">
          <IconButton
            size="small"
            onClick={() => onStartVideoCall?.(contact)}
            sx={{ color: embedded ? 'text.secondary' : MSN.titleBarText }}
          >
            <VideocamIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      {!minimized && isGroup && (
        activeGroupCall ? (
          <Tooltip title="Entrar na chamada">
            <IconButton
              size="small"
              onClick={() => onJoinGroupVideoCall?.(conversationId, groupName)}
              sx={{ color: MSN.online }}
            >
              <VideocamIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : (
          <Tooltip title="Iniciar chamada em grupo">
            <IconButton
              size="small"
              onClick={() => onStartGroupVideoCall?.(conversationId, groupName)}
              sx={{ color: embedded ? 'text.secondary' : MSN.titleBarText }}
            >
              <VideocamIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )
      )}
      {!embedded && onExpand && !minimized && (
        <Tooltip title="Abrir centro de comunicações">
          <IconButton size="small" onClick={onExpand} sx={{ color: MSN.titleBarText }}>
            <OpenInFullIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      {!embedded && onMinimize && (
        <Tooltip title={minimized ? 'Restaurar' : 'Minimizar'}>
          <IconButton
            size="small"
            onClick={onMinimize}
            sx={{ color: embedded ? 'text.secondary' : MSN.titleBarText }}
          >
            {minimized ? <OpenInFullIcon fontSize="small" /> : <RemoveIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
      )}
      {!embedded && (
        <IconButton
          size="small"
          onClick={onClose}
          sx={{ color: embedded ? 'text.secondary' : MSN.titleBarText }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      )}
    </Box>
  );

  // MSN Dock — só cabeçalho
  if (minimized && !embedded) {
    return (
      <Box
        sx={{
          width: 250,
          height: 40,
          display: 'flex',
          flexDirection: 'column',
          bgcolor: 'background.paper',
          borderRadius: 1,
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: '0 2px 10px rgba(15,23,42,0.15)',
          overflow: 'hidden'
        }}
      >
        {headerBar}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: embedded ? '100%' : width,
        height: embedded ? '100%' : height,
        display: 'flex',
        flexDirection: 'column',
        bgcolor: embedded ? 'grey.50' : MSN.windowBg,
        borderRadius: embedded ? 0 : 2,
        border: embedded ? 'none' : `2px solid ${MSN.border}`,
        boxShadow: embedded ? 'none' : '0 4px 20px rgba(0,0,0,0.15), inset 0 1px 0 rgba(255,255,255,0.8)',
        overflow: embedded ? 'hidden' : 'visible',
        position: 'relative',
        minWidth: 0,
        minHeight: 0
      }}
    >
      {headerBar}

      {!embedded && (
        <Box
          sx={{
            px: 1.5,
            py: 1,
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1,
            bgcolor: MSN.infoBannerYellow,
            borderBottom: `1px solid ${MSN.infoBannerYellowBorder}`,
            fontSize: 11,
            color: MSN.textPrimary,
            flexShrink: 0
          }}
        >
          <InfoIcon sx={{ fontSize: 16, color: MSN.buttonBg, mt: 0.25 }} />
          <span>Nunca revele sua senha ou dados de cartão em conversas.</span>
        </Box>
      )}

      <Box sx={{ display: 'flex', flex: 1, minHeight: 0, overflow: 'hidden' }}>
        <Box
          tabIndex={0}
          onPaste={handleConvPaste}
          onDragEnter={handleConvDragEnter}
          onDragLeave={handleConvDragLeave}
          onDragOver={handleConvDragOver}
          onDrop={handleConvDrop}
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            minWidth: 0,
            position: 'relative',
            outline: 'none',
            border: dropHover ? '2px dashed' : '2px solid transparent',
            borderColor: dropHover ? 'primary.main' : 'transparent',
            borderRadius: 1
          }}
        >
          {dropHover && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                zIndex: 5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'rgba(25, 118, 210, 0.1)',
                pointerEvents: 'none'
              }}
            >
              <Typography variant="subtitle2" color="primary.main" fontWeight={700}>
                Solte o arquivo para anexar
              </Typography>
            </Box>
          )}
          <MessageList
            messages={messages}
            currentUserId={currentUserId}
            contact={contact}
            isGroup={isGroup}
            typingLabel={typingLabel}
            markRead={markRead}
          />
          <Composer
            ref={composerRef}
            conversationId={conversationId}
            onSendMessage={onSendMessage}
            onTypingStart={sendTypingStart}
            onTypingStop={sendTypingStop}
            disabled={false}
          />
        </Box>

        {!embedded && (
          <Box
            sx={{
              width: 90,
              flexShrink: 0,
              borderLeft: `1px solid ${MSN.border}`,
              p: 1,
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              bgcolor: MSN.contentBg
            }}
          >
            <Box sx={{ textAlign: 'center' }}>
              <Box
                sx={{
                  display: 'inline-block',
                  borderRadius: 2,
                  border: `3px solid ${contactBorder}`,
                  p: 0.25,
                  overflow: 'hidden'
                }}
              >
                <Avatar
                  src={!isGroup && contact?.fotoPerfil ? getAuthenticatedUploadUrl(contact.fotoPerfil) : undefined}
                  sx={{ width: 64, height: 64, bgcolor: MSN.buttonBg, color: '#fff' }}
                >
                  {(!contact?.fotoPerfil || isGroup) &&
                    (isGroup ? (groupName || 'G') : (contact?.nome || '?')).charAt(0).toUpperCase()}
                </Avatar>
              </Box>
              <Typography variant="caption" sx={{ display: 'block', mt: 0.5, fontSize: 10, color: MSN.textSecondary }}>
                {title}
              </Typography>
            </Box>
            {currentUser && (
              <Box sx={{ textAlign: 'center' }}>
                <Box
                  sx={{
                    display: 'inline-block',
                    borderRadius: 2,
                    border: `3px solid ${userBorder}`,
                    p: 0.25,
                    overflow: 'hidden'
                  }}
                >
                  <Avatar
                    src={fotoPerfil ? getAuthenticatedUploadUrl(fotoPerfil) : undefined}
                    sx={{ width: 56, height: 56, bgcolor: MSN.buttonBg, color: '#fff', fontSize: 18 }}
                  >
                    {!fotoPerfil && (currentUser?.nome || currentUser?.email || '?').charAt(0).toUpperCase()}
                  </Avatar>
                </Box>
                <Typography variant="caption" sx={{ display: 'block', mt: 0.5, fontSize: 10, color: MSN.textSecondary }}>
                  Você
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </Box>

      {allowResize && (
        <>
          <Box
            onMouseDown={(e) => handleResizeStart(e, 'e')}
            sx={{
              position: 'absolute',
              top: 0,
              right: 0,
              width: 10,
              height: '100%',
              cursor: 'ew-resize',
              zIndex: 20
            }}
          />
          <Box
            onMouseDown={(e) => handleResizeStart(e, 's')}
            sx={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              width: '100%',
              height: 10,
              cursor: 'ns-resize',
              zIndex: 20
            }}
          />
          <Box
            onMouseDown={(e) => handleResizeStart(e, 'se')}
            sx={{
              position: 'absolute',
              bottom: 0,
              right: 0,
              width: 18,
              height: 18,
              cursor: 'nwse-resize',
              zIndex: 21
            }}
          />
        </>
      )}
    </Box>
  );
}
